import { EDatePresets } from 'app/consts/enums';
import { Moment } from 'moment';

export type DateRangePreset =
  | 'today'
  | 'yesterday'
  | 'tomorrow'
  | 'thisWeek'
  | 'lastWeek'
  | 'nextWeek'
  | 'last7'
  | 'thisMonth'
  | 'lastMonth'
  | 'nextMonth'
  | 'custom';

export interface DateRangePresetOption {
  value: DateRangePreset;
  label: string;
}

export const DATE_RANGE_PRESET_OPTIONS: ReadonlyArray<DateRangePresetOption> = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'tomorrow', label: 'Tomorrow' },
  { value: 'thisWeek', label: 'This week' },
  { value: 'lastWeek', label: 'Last week' },
  { value: 'nextWeek', label: 'Next week' },
  { value: 'last7', label: 'Last 7 days' },
  { value: 'thisMonth', label: 'This month' },
  { value: 'lastMonth', label: 'Last month' },
  { value: 'nextMonth', label: 'Next month' },
  { value: 'custom', label: 'Custom range' },
];

interface DateRangeResolver {
  getDateRange(
    preset: EDatePresets,
    referenceDate?: Moment
  ): { fromDate: Moment; toDate: Moment };
}
export function isDateRangePreset(value: unknown): value is DateRangePreset {
  return DATE_RANGE_PRESET_OPTIONS.some(option => option.value === value);
}

export function reconcileDateRangePreset(
  preset: unknown,
  fromDate: Moment | null | undefined,
  toDate: Moment | null | undefined,
  resolver: DateRangeResolver,
  referenceDate?: Moment | null
): DateRangePreset {
  if (!isDateRangePreset(preset) || preset === 'custom' || !fromDate || !toDate) {
    return 'custom';
  }

  const datePreset = toDatePreset(preset);
  if (datePreset === null) {
    return 'custom';
  }
  const expected = resolver.getDateRange(
    datePreset,
    referenceDate ? referenceDate.clone() : undefined
  );

  return fromDate.format('YYYY-MM-DD') ===
    expected.fromDate.format('YYYY-MM-DD') &&
    toDate.format('YYYY-MM-DD') === expected.toDate.format('YYYY-MM-DD')
    ? preset
    : 'custom';
}
export function toDatePreset(preset: DateRangePreset): EDatePresets | null {
  switch (preset) {
    case 'today':
      return EDatePresets.Today;
    case 'yesterday':
      return EDatePresets.Yesterday;
    case 'tomorrow':
      return EDatePresets.Tomorrow;
    case 'thisWeek':
      return EDatePresets.ThisWeek;
    case 'lastWeek':
      return EDatePresets.LastWeek;
    case 'nextWeek':
      return EDatePresets.NextWeek;
    case 'last7':
      return EDatePresets.LastSevenDays;
    case 'thisMonth':
      return EDatePresets.ThisMonth;
    case 'lastMonth':
      return EDatePresets.PreviousMonth;
    case 'nextMonth':
      return EDatePresets.NextMonth;
    case 'custom':
      return null;
  }
}
